// suggestion for subject reason
export interface Suggestion {
  message: string;
  urls?: Record<string, SuggestionUrl[] | string>;
}

export interface SuggestionUrl {
  message?: string;
  placeholder?: string;
  label?: string;
  url: string;
}
// suggestion single country, multiple urls for form.
export interface SuggestionByCountry {
  message: string;
  urls: SuggestionUrl[];
}

export const getSuggestionByCountry = (
  subjectKey: string,
  reasonKey: string,
  country: string,
): SuggestionByCountry | undefined => {
  const subject = suggestionMapping[subjectKey];
  const reason = subject[reasonKey];
  if (!reason || !reason.urls) {
    return undefined;
  }
  const suggestionUrl = reason.urls[country.toUpperCase()];
  const urls =
    typeof suggestionUrl === 'string'
      ? [
          {
            placeholder: 'HelpCentre',
            label: 'Help Centre',
            url: suggestionUrl,
          },
        ]
      : suggestionUrl;
  // At the moment we only support single messages for single url per country OR general messages for all countries
  const message = urls[0].message ?? reason.message;
  if (!urls) {
    return undefined;
  }
  return { message, urls };
};

export interface ExtendedSuggestion extends Suggestion {
  'I’ve stopped receiving e-mails'?: Suggestion;
  'Privacy & data protection'?: Suggestion;
  'Report a suspicious ad'?: Suggestion;
  'Login error/issue'?: Suggestion;
  'General feedback'?: Suggestion;
  'Job Advertisements'?: Suggestion;
  'Need help hiring'?: Suggestion;
  'Register for an advertiser account'?: Suggestion;
  'Cover letter'?: Suggestion;
  'Report a suspicious hirer'?: Suggestion;
  'I need to update my e-mail address'?: Suggestion;
  'Job posting error'?: Suggestion;
  'Product feedback'?: Suggestion;
  'Talent Search'?: Suggestion;
  'Bulk hiring'?: Suggestion;
  'How to post an Ad'?: Suggestion;
  'Withdrawal of application'?: Suggestion;
  'Unsubscribe from emails'?: Suggestion;
  'Talent Search error'?: Suggestion;
  'Company Reviews'?: Suggestion;
  'Branding opportunity'?: Suggestion;
  'Urgent hiring'?: Suggestion;
  'Products & pricing information'?: Suggestion;
  'Follow up application'?: Suggestion;
  'Job recommendation emails'?: Suggestion;
  JobMail?: Suggestion;
}

type InnerSuggestionMap = Record<string, Suggestion>;

export type SuggestionMapping = Record<string, InnerSuggestionMap>;

const signInReasonMapping = {
  'Forgot my username or password': {
    message: 'Need help signing in? Visit our %HelpCentre for more information',
    urls: {
      PH: 'https://help.ph.jobstreet.com/s/article/How-do-I-sign-in-to-my-account',
      MY: 'https://help.my.jobstreet.com/s/article/How-do-I-sign-in-to-my-account',
      SG: 'https://help.sg.jobstreet.com/s/article/How-do-I-sign-in-to-my-account',
      ID: 'https://help.id.jobstreet.com/id/s/article/How-do-I-sign-in-to-my-account?language=en_US',
      HK: 'https://help.hk.jobsdb.com/s/article/How-do-I-sign-in-to-my-account',
      TH: 'https://help.th.jobsdb.com/s/article/How-do-I-sign-in-to-my-account?language=en_US',
      AU: 'https://help.seek.com.au/s/article/How-do-I-sign-in-to-my-account',
      NZ: 'https://help.seek.co.nz/s/article/How-do-I-sign-in-to-my-account',
    },
  },
  'My account has been blocked': {
    message:
      'Your account has been blocked. Find out why by visiting our %HelpCentre',
    urls: {
      PH: 'https://help.ph.jobstreet.com/s/article/Why-is-my-account-locked-PH',
      MY: 'https://help.my.jobstreet.com/s/article/Why-is-My-Account-Locked-MY',
      SG: 'https://help.sg.jobstreet.com/s/article/Why-is-my-account-locked-SG',
      ID: 'https://help.id.jobstreet.com/id/s/article/Why-is-my-account-locked-ID?language=en_US',
      HK: 'https://help.hk.jobsdb.com/s/article/Why-is-my-account-locked-HK',
      TH: 'https://help.th.jobsdb.com/s/article/Why-is-my-account-locked-TH?language=en_US',
      AU: 'https://help.seek.com.au/s/article/Why-is-my-account-locked',
      NZ: 'https://help.seek.co.nz/s/article/Why-is-my-account-locked',
    },
  },
  'I didn’t receive my password reset e-mail': {
    message:
      'Having trouble receiving password reset emails? Our guide on %HelpCentre might help.',
    urls: {
      PH: 'https://help.ph.jobstreet.com/s/article/How-do-I-sign-in-to-my-account',
      MY: 'https://help.my.jobstreet.com/s/article/How-do-I-sign-in-to-my-account',
      SG: 'https://help.sg.jobstreet.com/s/article/How-do-I-sign-in-to-my-account',
      ID: 'https://help.id.jobstreet.com/id/s/article/How-do-I-sign-in-to-my-account?language=en_US',
      HK: 'https://help.hk.jobsdb.com/s/article/How-do-I-sign-in-to-my-account',
      TH: 'https://help.th.jobsdb.com/s/article/How-do-I-sign-in-to-my-account?language=en_US',
      AU: 'https://help.seek.com.au/s/article/How-do-I-sign-in-to-my-account',
      NZ: 'https://help.seek.co.nz/s/article/How-do-I-sign-in-to-my-account',
    },
  },
};

const suggestionMapping: SuggestionMapping = {
  '{brandName} Profile': {
    'What is a {brandName} Profile': {
      message:
        'Want to know more about your {brandName} Profile? Visit our %HelpCentre for information',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/What-is-a-Jobstreet-profile-PH',
        MY: 'https://help.my.jobstreet.com/s/article/What-is-a-Jobstreet-profile-MY',
        SG: 'https://help.sg.jobstreet.com/s/article/What-is-a-Jobstreet-profile-SG',
        ID: 'https://help.id.jobstreet.com/id/s/article/What-is-a-Jobstreet-profile-ID?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/What-is-a-JobsDB-profile-HK',
        TH: 'https://help.th.jobsdb.com/s/article/What-is-a-JobsDB-profile-TH?language=en_US',
        AU: 'https://help.seek.com.au/s/article/What-is-a-SEEK-profile',
        NZ: 'https://help.seek.co.nz/s/article/What-is-a-SEEK-profile',
      },
    },
    'Create or Upload a resumé': {
      message:
        'Need help creating or uploading a resume? Visit our %HelpCentre for information',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/How-do-I-create-a-Jobstreet-profile-JS',
        MY: 'https://help.my.jobstreet.com/s/article/How-do-I-create-a-Jobstreet-profile-JS',
        SG: 'https://help.sg.jobstreet.com/s/article/How-do-I-create-a-Jobstreet-profile-JS',
        ID: 'https://help.id.jobstreet.com/id/s/article/How-do-I-create-a-Jobstreet-profile-JS?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/How-do-I-create-a-JobsDB-profile-DB',
        TH: 'https://help.th.jobsdb.com/s/article/How-do-I-create-a-JobsDB-profile-DB?language=en_US',
        AU: 'https://help.seek.com.au/s/article/How-do-I-create-a-SEEK-profile',
        NZ: 'https://help.seek.co.nz/s/article/How-do-I-create-a-SEEK-profile',
      },
    },
    'Edit or Delete a resumé': {
      message:
        'Need help editing or deleting your resume? Visit our %HelpCentre for guidance',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/How-do-I-add-edit-delete-a-resume-PH',
        MY: 'https://help.my.jobstreet.com/s/article/How-do-I-add-edit-delete-a-resume-MY',
        SG: 'https://help.sg.jobstreet.com/s/article/How-do-I-add-edit-delete-a-resume-SG',
        ID: 'https://help.id.jobstreet.com/id/s/article/How-do-I-add-edit-delete-a-resume-ID?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/How-do-I-add-edit-delete-a-resume-HK',
        TH: 'https://help.th.jobsdb.com/s/article/How-do-I-add-edit-delete-a-resume-TH?language=en_US',
        AU: 'https://help.seek.com.au/s/article/How-do-I-add-edit-delete-a-resume',
        NZ: 'https://help.seek.co.nz/s/article/How-do-I-add-edit-delete-a-resume-NZ',
      },
    },
    'Update my profile': {
      message:
        'To update your profile, visit our %HelpCentre for more information',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/How-do-I-change-or-update-my-Profile-PH',
        MY: 'https://help.my.jobstreet.com/s/article/How-do-I-change-or-update-my-Profile-MY',
        SG: 'https://help.sg.jobstreet.com/s/article/How-do-I-change-or-update-my-Profile-SG',
        ID: 'https://help.id.jobstreet.com/id/s/article/How-do-I-change-or-update-my-Profile-ID?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/How-do-I-change-or-update-my-Profile-HK',
        TH: 'https://help.th.jobsdb.com/s/article/How-do-I-change-or-update-my-Profile-TH?language=en_US',
        AU: 'https://help.seek.com.au/s/article/How-do-I-change-or-update-my-Profile',
        NZ: 'https://help.seek.co.nz/s/article/How-do-I-change-or-update-my-Profile',
      },
    },
    'My profile visibility': {
      message:
        'Learn about your profile visibility options by visiting our %HelpCentre',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/Which-visibility-option-should-I-choose-PH',
        MY: 'https://help.my.jobstreet.com/s/article/Which-visibility-option-should-I-choose-MY',
        SG: 'https://help.sg.jobstreet.com/s/article/Which-visibility-option-should-I-choose-SG',
        ID: 'https://help.id.jobstreet.com/id/s/article/Which-visibility-option-should-I-choose-ID?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/Which-visibility-option-should-I-choose-HK',
        TH: 'https://help.th.jobsdb.com/s/article/Which-visibility-option-should-I-choose-TH?language=en_US',
        AU: 'https://help.seek.com.au/s/article/Which-visibility-option-should-I-choose',
        NZ: 'https://help.seek.co.nz/s/article/Which-visibility-option-should-I-choose',
      },
    },
    'Delete my profile': {
      message: 'Learn how to delete your profile by visiting our %HelpCentre',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/How-do-I-close-my-account-JS',
        MY: 'https://help.my.jobstreet.com/s/article/How-do-I-close-my-account-JS',
        SG: 'https://help.sg.jobstreet.com/s/article/How-do-I-close-my-account-JS',
        ID: 'https://help.id.jobstreet.com/id/s/article/How-do-I-close-my-account-JS?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/How-do-I-close-my-account-DB',
        TH: 'https://help.th.jobsdb.com/s/article/How-do-I-close-my-account-DB?language=en_US',
        AU: 'https://help.seek.com.au/s/article/How-do-I-close-my-account',
        NZ: 'https://help.seek.co.nz/s/article/How-do-I-close-my-account',
      },
    },
    'Profile sent with application': {
      message:
        'Discover how your profile is sent with applications by visiting our %HelpCentre',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/Which-resume-do-employers-see-when-they-view-my-Jobstreet-profile-PH',
        MY: 'https://help.my.jobstreet.com/s/article/Which-resume-do-employers-see-when-they-view-my-Jobstreet-profile-MY',
        SG: 'https://help.sg.jobstreet.com/s/article/Which-resume-do-employers-see-when-they-view-my-Jobstreet-profile-SG',
        ID: 'https://help.id.jobstreet.com/id/s/article/Which-resume-do-employers-see-when-they-view-my-Jobstreet-profile-ID?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/Which-resume-do-employers-see-when-they-view-my-JobsDB-profile-HK',
        TH: 'https://help.th.jobsdb.com/s/article/Which-resume-do-employers-see-when-they-view-my-JobsDB-profile-TH?language=en_US',
        AU: 'https://help.seek.com.au/s/article/Which-resume-do-employers-see-when-they-view-my-SEEK-profile',
        NZ: 'https://help.seek.co.nz/s/article/Which-resume-do-employers-see-when-they-view-my-SEEK-profile',
      },
    },
  },
  'Sign in / Password': {
    ...signInReasonMapping,
  },
  'Login / Password': {
    ...signInReasonMapping,
  },
  'My Account': {
    'Update account details': {
      message:
        'To learn how to update your account details, please visit our Help Centre articles on how to %UpdateNameAndPhone and %UpdateYourProfile',
      urls: {
        PH: [
          {
            placeholder: 'UpdateNameAndPhone',
            label: 'Update Name & Phone',
            url: 'https://help.ph.jobstreet.com/s/article/How-do-I-update-my-name-and-phone-number-JS',
          },
          {
            placeholder: 'UpdateYourProfile',
            label: 'Update Your Profile',
            url: 'https://help.ph.jobstreet.com/s/article/How-do-I-change-or-update-my-Profile-PH',
          },
        ],
        MY: [
          {
            placeholder: 'UpdateNameAndPhone',
            label: 'Update Name & Phone',
            url: 'https://help.my.jobstreet.com/s/article/How-do-I-update-my-name-and-phone-number-JS',
          },
          {
            placeholder: 'UpdateYourProfile',
            label: 'Update Your Profile',
            url: 'https://help.my.jobstreet.com/s/article/How-do-I-change-or-update-my-Profile-MY',
          },
        ],
        SG: [
          {
            placeholder: 'UpdateYourProfile',
            label: 'Update Your Profile',
            url: 'https://help.sg.jobstreet.com/s/article/How-do-I-change-or-update-my-Profile-SG',
          },
          {
            placeholder: 'UpdateNameAndPhone',
            label: 'Update Name & Phone',
            url: 'https://help.sg.jobstreet.com/s/article/How-do-I-update-my-name-and-phone-number-JS',
          },
        ],
        ID: [
          {
            placeholder: 'UpdateNameAndPhone',
            label: 'Update Name & Phone',
            url: 'https://help.id.jobstreet.com/id/s/article/How-do-I-update-my-name-and-phone-number-JS?language=en_US',
          },
          {
            placeholder: 'UpdateYourProfile',
            label: 'Update Your Profile',
            url: 'https://help.id.jobstreet.com/id/s/article/How-do-I-change-or-update-my-Profile-ID?language=en_US',
          },
        ],
        HK: [
          {
            placeholder: 'UpdateNameAndPhone',
            label: 'Update Name & Phone',
            url: 'https://help.hk.jobsdb.com/s/article/How-do-I-update-my-name-and-phone-number-DB',
          },
          {
            placeholder: 'UpdateYourProfile',
            label: 'Update Your Profile',
            url: 'https://help.hk.jobsdb.com/s/article/How-do-I-change-or-update-my-Profile-HK',
          },
        ],
        TH: [
          {
            placeholder: 'UpdateNameAndPhone',
            label: 'Update Name & Phone',
            url: 'https://help.th.jobsdb.com/s/article/How-do-I-update-my-name-and-phone-number-DB?language=en_US',
          },
          {
            placeholder: 'UpdateYourProfile',
            label: 'Update Your Profile',
            url: 'https://help.th.jobsdb.com/s/article/How-do-I-change-or-update-my-Profile-TH?language=en_US',
          },
        ],
        AU: [
          {
            placeholder: 'UpdateNameAndPhone',
            label: 'Update Name & Phone',
            url: 'https://help.seek.com.au/s/article/How-do-I-update-my-name-and-phone-number',
          },
          {
            placeholder: 'UpdateYourProfile',
            label: 'Update Your Profile',
            url: 'https://help.seek.com.au/s/article/How-do-I-change-or-update-my-Profile',
          },
        ],
        NZ: [
          {
            placeholder: 'UpdateNameAndPhone',
            label: 'Update Name & Phone',
            url: 'https://help.seek.co.nz/s/article/How-do-I-update-my-name-and-phone-number',
          },
          {
            placeholder: 'UpdateYourProfile',
            label: 'Update Your Profile',
            url: 'https://help.seek.co.nz/s/article/How-do-I-change-or-update-my-Profile',
          },
        ],
      },
    },
    'Update my e-mail address': {
      message:
        'Need to update your e-mail address? Visit our %HelpCentre for instructions',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/How-do-I-update-my-email-address-or-password-PH',
        MY: 'https://help.my.jobstreet.com/s/article/How-do-I-update-my-email-address-or-password-MY',
        SG: 'https://help.sg.jobstreet.com/s/article/How-do-I-update-my-email-address-or-password-SG',
        ID: 'https://help.id.jobstreet.com/id/s/article/How-do-I-update-my-email-address-or-password-ID?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/How-do-I-update-my-email-address-or-password-HK',
        TH: 'https://help.th.jobsdb.com/s/article/How-do-I-update-my-email-address-or-password-TH?language=en_US',
        AU: 'https://help.seek.com.au/s/article/How-do-I-update-my-email-address-or-password',
        NZ: 'https://help.seek.co.nz/s/article/How-do-I-update-my-email-address-or-password',
      },
    },
    Registration: {
      message:
        'Having trouble registering? Visit our %HelpCentre for help with the process',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/How-do-I-sign-in-or-register-for-Jobstreet-with-Facebook-Google-or-Apple-JS',
        MY: 'https://help.my.jobstreet.com/s/article/How-do-I-sign-in-or-register-for-Jobstreet-with-Facebook-Google-or-Apple-JS',
        SG: 'https://help.sg.jobstreet.com/s/article/How-do-I-sign-in-or-register-for-Jobstreet-with-Facebook-Google-or-Apple-JS',
        ID: 'https://help.id.jobstreet.com/id/s/article/How-do-I-sign-in-or-register-for-Jobstreet-with-Facebook-Google-or-Apple-JS?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/How-do-I-sign-in-or-register-for-JobsDB-with-Facebook-Google-or-Apple-DB',
        TH: 'https://help.th.jobsdb.com/s/article/How-do-I-sign-in-or-register-for-JobsDB-with-Facebook-Google-or-Apple-DB?language=en_US',
        AU: 'https://help.seek.com.au/s/article/How-do-I-sign-in-or-register-for-SEEK-with-Facebook-Google-or-Apple',
        NZ: 'https://help.seek.co.nz/s/article/How-do-I-sign-in-or-register-for-SEEK-with-Facebook-Google-or-Apple',
      },
    },
    'Applied Jobs': {
      message:
        'For information about your applied jobs, check out our Help Centre Articles on %ReceivedUpdates, %ApplicationIssues, and %AdditionalInfo',
      urls: {
        PH: [
          {
            placeholder: 'ReceivedUpdates',
            label: 'Received Updates',
            url: 'https://help.ph.jobstreet.com/s/article/Received-an-update-from-JobStreet-about-a-job-you-applied-for',
          },
          {
            placeholder: 'ApplicationIssues',
            label: 'Application Issues',
            url: 'https://help.ph.jobstreet.com/s/article/I-applied-for-a-job-that-took-me-to-the-employer-s-website-but-I-didn-t-complete-the-application-Why-is-it-listed-in-my-Applied-Jobs',
          },
          {
            placeholder: 'AdditionalInfo',
            label: 'Additional Information',
            url: 'https://help.ph.jobstreet.com/s/article/Looking-for-more-information-about-a-job-you-applied-for-JS',
          },
        ],
        MY: [
          {
            placeholder: 'ReceivedUpdates',
            label: 'Received Updates',
            url: 'https://help.my.jobstreet.com/s/article/Received-an-update-from-JobStreet-about-a-job-you-applied-for',
          },
          {
            placeholder: 'ApplicationIssues',
            label: 'Application Issues',
            url: 'https://help.my.jobstreet.com/s/article/I-applied-for-a-job-that-took-me-to-the-employer-s-website-but-I-didn-t-complete-the-application-Why-is-it-listed-in-my-Applied-Jobs',
          },
          {
            placeholder: 'AdditionalInfo',
            label: 'Additional Information',
            url: 'https://help.my.jobstreet.com/s/article/Looking-for-more-information-about-a-job-you-applied-for-JS',
          },
        ],
        SG: [
          {
            placeholder: 'ReceivedUpdates',
            label: 'Received Updates',
            url: 'https://help.sg.jobstreet.com/s/article/Received-an-update-from-JobStreet-about-a-job-you-applied-for',
          },
          {
            placeholder: 'ApplicationIssues',
            label: 'Application Issues',
            url: 'https://help.sg.jobstreet.com/s/article/I-applied-for-a-job-that-took-me-to-the-employer-s-website-but-I-didn-t-complete-the-application-Why-is-it-listed-in-my-Applied-Jobs',
          },
          {
            placeholder: 'AdditionalInfo',
            label: 'Additional Information',
            url: 'https://help.sg.jobstreet.com/s/article/Looking-for-more-information-about-a-job-you-applied-for-JS',
          },
        ],
        ID: [
          {
            placeholder: 'ReceivedUpdates',
            label: 'Received Updates',
            url: 'https://help.id.jobstreet.com/id/s/article/Received-an-update-from-JobStreet-about-a-job-you-applied-for?language=en_US',
          },
          {
            placeholder: 'ApplicationIssues',
            label: 'Application Issues',
            url: 'https://help.id.jobstreet.com/id/s/article/I-applied-for-a-job-that-took-me-to-the-employer-s-website-but-I-didn-t-complete-the-application-Why-is-it-listed-in-my-Applied-Jobs?language=en_US',
          },
          {
            placeholder: 'AdditionalInfo',
            label: 'Additional Information',
            url: 'https://help.id.jobstreet.com/id/s/article/Looking-for-more-information-about-a-job-you-applied-for-JS?language=en_US',
          },
        ],
        HK: [
          {
            placeholder: 'ReceivedUpdates',
            label: 'Received Updates',
            url: 'https://help.hk.jobsdb.com/s/article/Received-an-update-from-JobsDB-about-a-job-you-applied-for',
          },
          {
            placeholder: 'ApplicationIssues',
            label: 'Application Issues',
            url: 'https://help.hk.jobsdb.com/s/article/I-applied-for-a-job-that-took-me-to-the-employer-s-website-but-I-didn-t-complete-the-application-Why-is-it-listed-in-my-Applied-Jobs',
          },
          {
            placeholder: 'AdditionalInfo',
            label: 'Additional Information',
            url: 'https://help.hk.jobsdb.com/s/article/Looking-for-more-information-about-a-job-you-applied-for-DB',
          },
        ],
        TH: [
          {
            placeholder: 'ReceivedUpdates',
            label: 'Received Updates',
            url: 'https://help.th.jobsdb.com/s/article/Received-an-update-from-JobsDB-about-a-job-you-applied-for?language=en_US',
          },
          {
            placeholder: 'ApplicationIssues',
            label: 'Application Issues',
            url: 'https://help.th.jobsdb.com/s/article/I-applied-for-a-job-that-took-me-to-the-employer-s-website-but-I-didn-t-complete-the-application-Why-is-it-listed-in-my-Applied-Jobs?language=en_US',
          },
          {
            placeholder: 'AdditionalInfo',
            label: 'Additional Information',
            url: 'https://help.th.jobsdb.com/s/article/Looking-for-more-information-about-a-job-you-applied-for-DB?language=en_US',
          },
        ],
        AU: [
          {
            placeholder: 'AdditionalInfo',
            label: 'Additional Information',
            url: 'https://help.seek.com.au/s/article/Looking-for-more-information-about-a-job-you-applied-for',
          },
          {
            placeholder: 'ApplicationIssues',
            label: 'Application Issues',
            url: 'https://help.seek.com.au/s/article/I-can-see-a-job-I-applied-for-in-my-Applied-Jobs-list-but-I-can-t-find-a-copy-of-the-resume-and-cover-letter-I-submitted',
          },
          {
            placeholder: 'ReceivedUpdates',
            label: 'Received Updates',
            url: 'https://help.seek.com.au/s/article/Received-an-update-from-SEEK-about-a-job-you-applied-for',
          },
        ],
        NZ: [
          {
            placeholder: 'AdditionalInfo',
            label: 'Additional Information',
            url: 'https://help.seek.co.nz/s/article/Looking-for-more-information-about-a-job-you-applied-for',
          },
          {
            placeholder: 'ApplicationIssues',
            label: 'Application Issues',
            url: 'https://help.seek.co.nz/s/article/I-can-see-a-job-I-applied-for-in-my-Applied-Jobs-list-but-I-can-t-find-a-copy-of-the-resume-and-cover-letter-I-submitted',
          },
          {
            placeholder: 'ReceivedUpdates',
            label: 'Received Updates',
            url: 'https://help.seek.co.nz/s/article/Received-an-update-from-SEEK-about-a-job-you-applied-for',
          },
        ],
      },
    },
    'Saved jobs': {
      message:
        'Learn how to save jobs by visiting our %SaveJob and %SaveSearch Help Centre articles',
      urls: {
        PH: [
          {
            placeholder: 'SaveJob',
            label: 'How to Save a Job',
            url: 'https://help.ph.jobstreet.com/s/article/How-do-I-save-a-job-JS',
          },
          {
            placeholder: 'SaveSearch',
            label: 'How to Save a Search',
            url: 'https://help.ph.jobstreet.com/s/article/How-do-I-save-a-search-PH',
          },
        ],
        MY: [
          {
            placeholder: 'SaveSearch',
            label: 'How to Save a Search',
            url: 'https://help.my.jobstreet.com/s/article/How-do-I-save-a-search-MY',
          },
          {
            placeholder: 'SaveJob',
            label: 'How to Save a Job',
            url: 'https://help.my.jobstreet.com/s/article/How-do-I-save-a-job-JS',
          },
        ],
        SG: [
          {
            placeholder: 'SaveJob',
            label: 'How to Save a Job',
            url: 'https://help.sg.jobstreet.com/s/article/How-do-I-save-a-job-JS',
          },
          {
            placeholder: 'SaveSearch',
            label: 'How to Save a Search',
            url: 'https://help.sg.jobstreet.com/s/article/How-do-I-save-a-search-SG',
          },
        ],
        ID: [
          {
            placeholder: 'SaveJob',
            label: 'How to Save a Job',
            url: 'https://help.id.jobstreet.com/id/s/article/How-do-I-save-a-job-JS?language=en_US',
          },
          {
            placeholder: 'SaveSearch',
            label: 'How to Save a Search',
            url: 'https://help.id.jobstreet.com/id/s/article/How-do-I-save-a-search-ID?language=en_US',
          },
        ],
        HK: [
          {
            placeholder: 'SaveJob',
            label: 'How to Save a Job',
            url: 'https://help.hk.jobsdb.com/s/article/How-do-I-save-a-job-DB',
          },
          {
            placeholder: 'SaveSearch',
            label: 'How to Save a Search',
            url: 'https://help.hk.jobsdb.com/s/article/How-do-I-save-a-search-HK',
          },
        ],
        TH: [
          {
            placeholder: 'SaveJob',
            label: 'How to Save a Job',
            url: 'https://help.th.jobsdb.com/s/article/How-do-I-save-a-job-DB?language=en_US',
          },
          {
            placeholder: 'SaveSearch',
            label: 'How to Save a Search',
            url: 'https://help.th.jobsdb.com/s/article/How-do-I-save-a-search-TH?language=en_US',
          },
        ],
        AU: [
          {
            placeholder: 'SaveJob',
            label: 'How to Save a Job',
            url: 'https://help.seek.com.au/s/article/How-do-I-save-a-job',
          },
          {
            placeholder: 'SaveSearch',
            label: 'How to Save a Search',
            url: 'https://help.seek.com.au/s/article/How-do-I-save-a-search',
          },
        ],
        NZ: [
          {
            placeholder: 'SaveJob',
            label: 'How to Save a Job',
            url: 'https://help.seek.co.nz/s/article/How-do-I-save-a-job',
          },
          {
            placeholder: 'SaveSearch',
            label: 'How to Save a Search',
            url: 'https://help.seek.co.nz/s/article/How-do-I-save-a-search',
          },
        ],
      },
    },
    'Delete account': {
      message: 'Want to delete your account? Visit our %HelpCentre for details',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/How-do-I-close-my-account-JS',
        MY: 'https://help.my.jobstreet.com/s/article/How-do-I-close-my-account-JS',
        SG: 'https://help.sg.jobstreet.com/s/article/How-do-I-close-my-account-JS',
        ID: 'https://help.id.jobstreet.com/id/s/article/How-do-I-close-my-account-JS?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/How-do-I-close-my-account-DB',
        TH: 'https://help.th.jobsdb.com/s/article/How-do-I-close-my-account-DB?language=en_US',
        AU: 'https://help.seek.com.au/s/article/How-do-I-close-my-account',
        NZ: 'https://help.seek.co.nz/s/article/How-do-I-close-my-account',
      },
    },
  },
  'Job Search': {
    'How to search for a job': {
      message:
        'Discover the best tips for job searching by visiting our %HelpCentre',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/What-s-the-best-way-to-search-for-a-job',
        MY: 'https://help.my.jobstreet.com/s/article/What-s-the-best-way-to-search-for-a-job',
        SG: 'https://help.sg.jobstreet.com/s/article/What-s-the-best-way-to-search-for-a-job',
        ID: 'https://help.id.jobstreet.com/id/s/article/What-s-the-best-way-to-search-for-a-job?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/What-s-the-best-way-to-search-for-a-job',
        TH: 'https://help.th.jobsdb.com/s/article/What-s-the-best-way-to-search-for-a-job?language=en_US',
        AU: 'https://help.seek.com.au/s/article/What-s-the-best-way-to-search-for-a-job',
        NZ: 'https://help.seek.co.nz/s/article/What-s-the-best-way-to-search-for-a-job',
      },
    },
    'Job search issues/error': {
      message:
        'Having trouble with job search? Visit our %HelpCentre for troubleshooting',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page-PH',
        MY: 'https://help.my.jobstreet.com/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page-MY',
        SG: 'https://help.sg.jobstreet.com/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page-SG',
        ID: 'https://help.id.jobstreet.com/id/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page-ID?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page-HK',
        TH: 'https://help.th.jobsdb.com/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page-TH?language=en_US',
        AU: 'https://help.seek.com.au/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page',
        NZ: 'https://help.seek.co.nz/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page',
      },
    },
  },
  'Applying for a job': {
    'Application error': {
      message:
        'Encountering an error with your application? Visit our %HelpCentre for help',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page-PH',
        MY: 'https://help.my.jobstreet.com/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page-MY',
        SG: 'https://help.sg.jobstreet.com/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page-SG',
        ID: 'https://help.id.jobstreet.com/id/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page-ID?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page-HK',
        TH: 'https://help.th.jobsdb.com/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page-TH?language=en_US',
        AU: 'https://help.seek.com.au/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page',
        NZ: 'https://help.seek.co.nz/s/article/Seeing-an-error-message-or-having-trouble-loading-a-page',
      },
    },
    'How to apply': {
      message:
        'Need help applying for a job? Visit our %HelpCentre for step-by-step instructions',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/How-do-I-apply-for-a-job-on-Jobstreet-PH',
        MY: 'https://help.my.jobstreet.com/s/article/How-do-I-apply-for-a-job-on-Jobstreet-MY',
        SG: 'https://help.sg.jobstreet.com/s/article/How-do-I-apply-for-a-job-on-Jobstreet-SG',
        ID: 'https://help.id.jobstreet.com/id/s/article/How-do-I-apply-for-a-job-on-Jobstreet-ID?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/How-do-I-apply-for-a-job-on-JobsDB-HK',
        TH: 'https://help.th.jobsdb.com/s/article/How-do-I-apply-for-a-job-on-JobsDB-TH?language=en_US',
        AU: 'https://help.seek.com.au/s/article/How-do-I-apply-for-a-job-on-SEEK',
        NZ: 'https://help.seek.co.nz/s/article/How-do-I-apply-for-a-job-on-SEEK',
      },
    },
    'Privacy & data protection': {
      message:
        "To learn how your privacy is protected, see the privacy policy updates %PrivacyPolicy, and review dos and don'ts of protecting your privacy while job-hunting %PrivacyTips",
      urls: {
        PH: [
          {
            placeholder: 'PrivacyPolicy',
            label: 'Privacy Policy Updates',
            url: 'https://help.ph.jobstreet.com/s/article/Privacy-Policy-Updates-PH',
          },
          {
            placeholder: 'PrivacyTips',
            label: 'Privacy Tips',
            url: 'https://help.ph.jobstreet.com/s/article/The-dos-and-don-ts-of-protecting-your-privacy-while-job-hunting',
          },
        ],
        MY: [
          {
            placeholder: 'PrivacyPolicy',
            label: 'Privacy Policy Updates',
            url: 'https://help.my.jobstreet.com/s/article/Privacy-Policy-Updates-MY',
          },
          {
            placeholder: 'PrivacyTips',
            label: 'Privacy Tips',
            url: 'https://help.my.jobstreet.com/s/article/The-dos-and-don-ts-of-protecting-your-privacy-while-job-hunting',
          },
        ],
        SG: [
          {
            placeholder: 'PrivacyPolicy',
            label: 'Privacy Policy Updates',
            url: 'https://help.sg.jobstreet.com/s/article/Privacy-Policy-Updates-SG',
          },
          {
            placeholder: 'PrivacyTips',
            label: 'Privacy Tips',
            url: 'https://help.sg.jobstreet.com/s/article/The-dos-and-don-ts-of-protecting-your-privacy-while-job-hunting',
          },
        ],
        ID: [
          {
            placeholder: 'PrivacyPolicy',
            label: 'Privacy Policy Updates',
            url: 'https://help.id.jobstreet.com/id/s/article/Privacy-Policy-Updates-ID?language=en_US',
          },
          {
            placeholder: 'PrivacyTips',
            label: 'Privacy Tips',
            url: 'https://help.id.jobstreet.com/id/s/article/The-dos-and-don-ts-of-protecting-your-privacy-while-job-hunting?language=en_US',
          },
        ],
        HK: [
          {
            placeholder: 'HelpCenter',
            label: 'Help Centre',
            url: 'https://help.hk.jobsdb.com/s/article/The-dos-and-don-ts-of-protecting-your-privacy-while-job-hunting',
            message:
              "To review the dos and don'ts of protecting your privacy while job-hunting, visit our %HelpCenter.",
          },
        ],
        TH: [
          {
            placeholder: 'HelpCenter',
            label: 'Help Centre',
            url: 'https://help.th.jobsdb.com/s/article/The-dos-and-don-ts-of-protecting-your-privacy-while-job-hunting?language=en_US',
            message:
              "To review the dos and don'ts of protecting your privacy while job-hunting, visit our %HelpCenter.",
          },
        ],
        AU: [
          {
            placeholder: 'PrivacyPolicy',
            label: 'Privacy Policy',
            url: 'https://help.seek.com.au/s/article/What-s-SEEK-doing-to-protect-my-information',
            message:
              'To learn how your privacy is protected, see the %PrivacyPolicy updates.',
          },
        ],
        NZ: [
          {
            placeholder: 'PrivacyPolicy',
            label: 'Privacy Policy',
            url: 'https://help.seek.co.nz/s/article/What-s-SEEK-doing-to-protect-my-information',
            message:
              'To learn how your privacy is protected, see the %PrivacyPolicy updates.',
          },
        ],
      },
    },
    'Cover letter': {
      message:
        'Need help composing your cover letter? Visit our %HelpCentre for tips',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/What-is-a-cover-letter-ASIA',
        MY: 'https://help.my.jobstreet.com/s/article/What-is-a-cover-letter-ASIA',
        SG: 'https://help.sg.jobstreet.com/s/article/What-is-a-cover-letter-ASIA',
        ID: 'https://help.id.jobstreet.com/id/s/article/What-is-a-cover-letter-ASIA?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/What-is-a-cover-letter-ASIA',
        TH: 'https://help.th.jobsdb.com/s/article/What-is-a-cover-letter-ASIA?language=en_US',
        AU: 'https://help.seek.com.au/s/article/What-is-a-cover-letter',
        NZ: 'https://help.seek.co.nz/s/article/What-is-a-cover-letter',
      },
    },
    'Withdrawal of application': {
      message:
        'Need to withdraw your application? Visit our %HelpCentre for instructions',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/How-do-I-withdraw-or-cancel-my-application-JS',
        MY: 'https://help.my.jobstreet.com/s/article/How-do-I-withdraw-or-cancel-my-application-JS',
        SG: 'https://help.sg.jobstreet.com/s/article/How-do-I-withdraw-or-cancel-my-application-JS',
        ID: 'https://help.id.jobstreet.com/id/s/article/How-do-I-withdraw-or-cancel-my-application-JS?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/How-do-I-withdraw-or-cancel-my-application-DB',
        TH: 'https://help.th.jobsdb.com/s/article/How-do-I-withdraw-or-cancel-my-application-DB?language=en_US',
        AU: 'https://help.seek.com.au/s/article/How-do-I-withdraw-or-cancel-my-application',
        NZ: 'https://help.seek.co.nz/s/article/How-do-I-withdraw-or-cancel-my-application',
      },
    },
    'Follow up application': {
      message:
        'Want to follow up on your application? Visit our %HelpCentre for advice',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/Received-an-update-from-JobStreet-about-a-job-you-applied-for',
        MY: 'https://help.my.jobstreet.com/s/article/Received-an-update-from-JobStreet-about-a-job-you-applied-for',
        SG: 'https://help.sg.jobstreet.com/s/article/Received-an-update-from-JobStreet-about-a-job-you-applied-for',
        ID: 'https://help.id.jobstreet.com/id/s/article/Received-an-update-from-JobStreet-about-a-job-you-applied-for?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/Received-an-update-from-JobsDB-about-a-job-you-applied-for',
        TH: 'https://help.th.jobsdb.com/s/article/Received-an-update-from-JobsDB-about-a-job-you-applied-for?language=en_US',
        AU: 'https://help.seek.com.au/s/article/Received-an-update-from-SEEK-about-a-job-you-applied-for',
        NZ: 'https://help.seek.co.nz/s/article/Received-an-update-from-SEEK-about-a-job-you-applied-for',
      },
    },
  },
  'Report a suspicious ad': {
    'Report a suspicious ad': {
      message:
        'To report a suspicious ad, please refer to our Help Centre Articles: %ReportSuspiciousAd and %NoticedSuspiciousActivity',
      urls: {
        PH: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.ph.jobstreet.com/s/article/How-do-I-report-a-suspicious-or-offensive-ad-JS',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.ph.jobstreet.com/s/article/Noticed-suspicious-activity-JS',
          },
        ],
        MY: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.my.jobstreet.com/s/article/How-do-I-report-a-suspicious-or-offensive-ad-JS',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.my.jobstreet.com/s/article/Noticed-suspicious-activity-JS',
          },
        ],
        SG: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.sg.jobstreet.com/s/article/How-do-I-report-a-suspicious-or-offensive-ad-JS',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.sg.jobstreet.com/s/article/Noticed-suspicious-activity-JS',
          },
        ],
        ID: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.id.jobstreet.com/id/s/article/How-do-I-report-a-suspicious-or-offensive-ad-JS?language=en_US',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.id.jobstreet.com/id/s/article/Noticed-suspicious-activity-JS?language=en_US',
          },
        ],
        HK: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.hk.jobsdb.com/s/article/How-do-I-report-a-suspicious-or-offensive-ad-DB',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.hk.jobsdb.com/s/article/Noticed-suspicious-activity-DB',
          },
        ],
        TH: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.th.jobsdb.com/s/article/How-do-I-report-a-suspicious-or-offensive-ad-DB?language=en_US',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.th.jobsdb.com/s/article/Noticed-suspicious-activity-DB?language=en_US',
          },
        ],
        AU: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.seek.com.au/s/article/How-do-I-report-a-suspicious-or-offensive-ad',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.seek.com.au/s/article/Noticed-suspicious-activity',
          },
        ],
        NZ: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.seek.co.nz/s/article/How-do-I-report-a-suspicious-or-offensive-ad',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.seek.co.nz/s/article/Noticed-suspicious-activity',
          },
        ],
      },
    },
    'Report a suspicious hirer': {
      message:
        'To report a suspicious hirer, please refer to our Help Centre Articles: %ReportSuspiciousAd and %NoticedSuspiciousActivity',
      urls: {
        PH: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.ph.jobstreet.com/s/article/How-do-I-report-a-suspicious-or-offensive-ad-JS',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.ph.jobstreet.com/s/article/Noticed-suspicious-activity-JS',
          },
        ],
        MY: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.my.jobstreet.com/s/article/How-do-I-report-a-suspicious-or-offensive-ad-JS',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.my.jobstreet.com/s/article/Noticed-suspicious-activity-JS',
          },
        ],
        SG: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.sg.jobstreet.com/s/article/How-do-I-report-a-suspicious-or-offensive-ad-JS',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.sg.jobstreet.com/s/article/Noticed-suspicious-activity-JS',
          },
        ],
        ID: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.id.jobstreet.com/id/s/article/How-do-I-report-a-suspicious-or-offensive-ad-JS?language=en_US',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.id.jobstreet.com/id/s/article/Noticed-suspicious-activity-JS?language=en_US',
          },
        ],
        HK: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.hk.jobsdb.com/s/article/How-do-I-report-a-suspicious-or-offensive-ad-DB',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.hk.jobsdb.com/s/article/Noticed-suspicious-activity-DB',
          },
        ],
        TH: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.th.jobsdb.com/s/article/How-do-I-report-a-suspicious-or-offensive-ad-DB?language=en_US',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.th.jobsdb.com/s/article/Noticed-suspicious-activity-DB?language=en_US',
          },
        ],
        AU: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.seek.com.au/s/article/How-do-I-report-a-suspicious-or-offensive-ad',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.seek.com.au/s/article/Noticed-suspicious-activity',
          },
        ],
        NZ: [
          {
            placeholder: 'ReportSuspiciousAd',
            label: 'Report Suspicious/Offensive Ad',
            url: 'https://help.seek.co.nz/s/article/How-do-I-report-a-suspicious-or-offensive-ad',
          },
          {
            placeholder: 'NoticedSuspiciousActivity',
            label: 'Noticed Suspicious Activity',
            url: 'https://help.seek.co.nz/s/article/Noticed-suspicious-activity',
          },
        ],
      },
    },
  },
  'Email Issues': {
    'I’ve stopped receiving e-mails': {
      message:
        'Not receiving emails? Visit our %HelpCentre to learn how to resolve the issue',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/Help-Why-have-I-stopped-receiving-my-JobStreet-Job-Alerts',
        MY: 'https://help.my.jobstreet.com/s/article/Help-Why-have-I-stopped-receiving-my-JobStreet-Job-Alerts',
        SG: 'https://help.sg.jobstreet.com/s/article/Help-Why-have-I-stopped-receiving-my-JobStreet-Job-Alerts',
        ID: 'https://help.id.jobstreet.com/id/s/article/Help-Why-have-I-stopped-receiving-my-JobStreet-Job-Alerts?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/Help-Why-have-I-stopped-receiving-my-JobsDB-Job-Alerts',
        TH: 'https://help.th.jobsdb.com/s/article/Help-Why-have-I-stopped-receiving-my-JobsDB-Job-Alerts?language=en_US',
        AU: 'https://help.seek.com.au/s/article/Help-Why-have-I-stopped-receiving-my-SEEK-Job-Alerts',
        NZ: 'https://help.seek.co.nz/s/article/Help-Why-have-I-stopped-receiving-my-SEEK-Job-Alerts',
      },
    },
    'I need to update my e-mail address': {
      message:
        'Need to update your e-mail address? Visit our %HelpCentre for instructions',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/How-do-I-update-my-email-address-or-password-PH',
        MY: 'https://help.my.jobstreet.com/s/article/How-do-I-update-my-email-address-or-password-MY',
        SG: 'https://help.sg.jobstreet.com/s/article/How-do-I-update-my-email-address-or-password-SG',
        ID: 'https://help.id.jobstreet.com/id/s/article/How-do-I-update-my-email-address-or-password-ID?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/How-do-I-update-my-email-address-or-password-HK',
        TH: 'https://help.th.jobsdb.com/s/article/How-do-I-update-my-email-address-or-password-TH?language=en_US',
        AU: 'https://help.seek.com.au/s/article/How-do-I-update-my-email-address-or-password',
        NZ: 'https://help.seek.co.nz/s/article/How-do-I-update-my-email-address-or-password',
      },
    },
    'Unsubscribe from emails': {
      message:
        'Want to unsubscribe from emails? Visit our %HelpCentre for instructions',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/How-do-I-unsubscribe-from-Jobstreet-emails-and-notifications-PH',
        MY: 'https://help.my.jobstreet.com/s/article/How-do-I-unsubscribe-from-Jobstreet-emails-and-notifications-MY',
        SG: 'https://help.sg.jobstreet.com/s/article/How-do-I-unsubscribe-from-Jobstreet-emails-and-notifications-SG',
        ID: 'https://help.id.jobstreet.com/id/s/article/How-do-I-unsubscribe-from-Jobstreet-emails-and-notifications-ID?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/How-do-I-unsubscribe-from-JobsDB-emails-and-notifications-HK',
        TH: 'https://help.th.jobsdb.com/s/article/How-do-I-unsubscribe-from-JobsDB-emails-and-notifications-TH?language=en_US',
        AU: 'https://help.seek.com.au/s/article/How-do-I-unsubscribe-from-SEEK-emails-and-notifications',
        NZ: 'https://help.seek.co.nz/s/article/How-do-I-unsubscribe-from-SEEK-emails-and-notifications',
      },
    },
    'Job recommendation emails': {
      message:
        'Learn about job recommendation emails by visiting our %HelpCentre',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/Want-to-know-more-about-Jobstreet-Recommendations-JS',
        MY: 'https://help.my.jobstreet.com/s/article/Want-to-know-more-about-Jobstreet-Recommendations-JS',
        SG: 'https://help.sg.jobstreet.com/s/article/Want-to-know-more-about-Jobstreet-Recommendations-JS',
        ID: 'https://help.id.jobstreet.com/id/s/article/Want-to-know-more-about-Jobstreet-Recommendations-JS?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/Want-to-know-more-about-JobsDB-Recommendations-HK',
        TH: 'https://help.th.jobsdb.com/s/article/Want-to-know-more-about-JobsDB-Recommendations-TH?language=en_US',
      },
    },
    JobMail: {
      message:
        'Learn about job recommendation emails by visiting our %HelpCentre',
      urls: {
        AU: 'https://help.seek.com.au/s/article/Want-to-know-more-about-SEEK-Recommendations',
        NZ: 'https://help.seek.co.nz/s/article/Want-to-know-more-about-SEEK-Recommendations',
      },
    },
  },
  Other: {
    'Company Reviews': {
      message: 'Learn more about company reviews by visiting our %HelpCentre',
      urls: {
        PH: 'https://help.ph.jobstreet.com/s/article/Company-reviews-community-guidelines-asia',
        MY: 'https://help.my.jobstreet.com/s/article/Company-reviews-community-guidelines-asia',
        SG: 'https://help.sg.jobstreet.com/s/article/Company-reviews-community-guidelines-asia',
        ID: 'https://help.id.jobstreet.com/id/s/article/Company-reviews-community-guidelines-asia?language=en_US',
        HK: 'https://help.hk.jobsdb.com/s/article/company-reviews-community-guidelines-DB',
        TH: 'https://help.th.jobsdb.com/s/article/company-reviews-community-guidelines-DB?language=en_US',
        AU: 'https://help.seek.com.au/s/article/Company-reviews-community-guidelines',
        NZ: 'https://help.seek.co.nz/s/article/Company-reviews-community-guidelines',
      },
    },
  },
};
